// Frontend-local shared control: localization changes presentation only.
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { useEffect } from "react";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { IconClose } from "@ui/icons/IconClose";
import Spacer from "@ui/spacer/Spacer";
import { Spinner } from "@ui/spinner/Spinner";
import { Typo, TypoSmall } from "@ui/text/Typo";
import { RecursiveCSS } from "@ui/theme/GlobalTheme";

interface IPanelProps {
	children?: any;
	scrollable?: boolean;
	decorator?: "absolute";
	busy?: boolean;
	label?: string;
	sx?: RecursiveCSS;
	onClose?: () => void;
	contentPadding?: string;
}

const NormalPanel = ({ children, onClose, label, scrollable, decorator, busy, sx, contentPadding }: IPanelProps) => {
	useLocale();
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose?.();
			}
		};

		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [onClose]);

	if (decorator === "absolute" && scrollable) {
		children = (
			<VBox
				style={{
					flexGrow: "1",
					overflow: "auto",
				}}>
				{children}
			</VBox>
		);
	}

	const childrenContent = label ? (
		<VBox
			style={{
				padding: contentPadding ?? "20px",
				flexGrow: "1",
			}}>
			{children}
		</VBox>
	) : (
		children
	);

	const content = (
		<Box
			sx={{
				border: "1px solid rgba(255, 255, 255, 0.05)",
				padding: !label ? "30px" : undefined,
				borderRadius: "6px",
				boxShadow: "0px 0px 100px rgba(0,0,0,.1)",
				pointerEvents: busy ? "none" : "auto",
				userSelect: busy ? "none" : "auto",
				display: "flex",
				position: "relative",
				flexDirection: "column",
				backgroundColor: "panelBackgroundPrimary",
				maxHeight: "95vh",

				...sx,
			}}>
			{busy && (
				<Box
					sx={{
						position: "absolute",
						top: "0px",
						left: "0px",
						width: "100%",
						height: "100%",
						backgroundColor: "rgba(255,255,255,.5)",
						zIndex: 100000,
						display: "flex",
						flexDirection: "column",
						gap: "10px",
						justifyContent: "center",
						alignItems: "center",
					}}>
					<Spinner color="#ad1bf1" />
					<TypoSmall>{msg("sharedPleasewait")}</TypoSmall>
				</Box>
			)}
			{label && (
				<>
					<HBox
						sx={{
							backgroundColor: decorator === "absolute" ? "panelHeadBackgroundColor" : undefined,
							padding: "20px",
							boxSizing: "border-box",
							width: "100%",
							alignItems: "center",
						}}>
						<Typo
							variant="head"
							sx={{
								display: "flex",
								paddingRight: "30px",
								overflowWrap: "break-word",
								wordBreak: "break-all",
								width: "100%",
								fontWeight: decorator === "absolute" ? "200" : "500",
								fontSize: decorator === "absolute" ? "1.4rem" : "1.2rem",
							}}>
							{label}
						</Typo>
						{onClose && (
							<>
								<Spacer grow />
								<Button variant="icon" icon={<IconClose />} sx={{ padding: "10px !important" }} onClick={onClose} />
							</>
						)}
					</HBox>
					<Box
						sx={{
							boxSizing: "border-box",
							background: "panelDividerBackgroundColor",
							height: "2px",
							maxHeight: "2px",
						}}
					/>
				</>
			)}
			{childrenContent}
			{busy && (
				<Box
					sx={{
						position: "absolute",
						top: "0px",
						left: "0px",
						width: "100%",
						height: "100%",
						backdropFilter: "saturate(0.2)",
						zIndex: 100001,
					}}
				/>
			)}
		</Box>
	);

	let panel = <>{content}</>;

	if (decorator === "absolute") {
		panel = (
			<Box
				sx={{
					position: "fixed",
					top: "0",
					left: "0",
					width: "100%",
					height: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					zIndex: 1000,
					backgroundColor: "rgba(0,0,0,.6)",
				}}>
				{content}
			</Box>
		);
	}

	return panel;
};

export default NormalPanel;
